import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sailboat, Car, Music, Waves, Hotel, Utensils, Wifi, Sun } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Billet } from "@/components/site/Billet";
import { IMG } from "@/components/site/content";
import { useLang } from "@/context/LanguageContext";

const ICONS = { Sailboat, Car, Music, Waves };
const CHIP_ICONS = [Sun, Waves, Hotel];
const HOTEL_ICONS = [Utensils, Waves, Wifi, Hotel];

export default function Destination() {
  const { t } = useLang();
  const p = t.punta;
  const ho = t.hotel;
  const ex = t.excursions;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <>
      {/* 5. Punta Cana */}
      <section
        id="punta-cana"
        ref={ref}
        className="relative flex min-h-[80vh] items-center justify-center overflow-hidden bg-[#14283A]"
        data-testid="section-punta-cana"
      >
        <motion.img src={IMG.location} alt="" style={{ y }} className="absolute inset-0 h-[124%] w-full object-cover" />
        <div className="absolute inset-0 bg-[#14283A]/55" />
        <div className="relative z-10 mx-auto max-w-4xl px-5 text-center text-[#F6F1E8]">
          <Reveal>
            <span className="font-marker text-xl text-[#EA7A4F]">{p.kicker}</span>
            <h2 className="mt-3 font-display text-5xl leading-[0.95] sm:text-7xl">{p.title}</h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-[#F6F1E8]/90">{p.body}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm font-semibold">
              {p.chips.map((c, i) => {
                const Icon = CHIP_ICONS[i] || Sun;
                return (
                  <span key={i} className="flex items-center gap-2 rounded-full border border-[#F6F1E8]/30 px-4 py-2">
                    <Icon className="h-4 w-4 text-[#EA7A4F]" /> {c}
                  </span>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. L'hôtel */}
      <section className="bg-[#F6F1E8] py-24 sm:py-32" data-testid="section-hotel">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 lg:grid-cols-2">
          <div>
            <span className="font-marker text-lg text-[#3F9BA6]">{ho.kicker}</span>
            <Reveal>
              <h2 className="mt-2 font-display text-4xl leading-[0.95] text-[#14283A] sm:text-6xl">
                {ho.titlePre} <span className="grad-underline">{ho.titleAccent}</span> {ho.titlePost}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-lg text-lg text-[#14283A]/80">{ho.body}</p>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-8 grid grid-cols-2 gap-4">
                {ho.cards.map(([tt, d], i) => {
                  const Icon = HOTEL_ICONS[i] || Hotel;
                  return (
                    <div key={tt} className="rounded-xl border-2 border-[#14283A]/10 bg-white p-4">
                      <Icon className="h-6 w-6 text-[#EA7A4F]" />
                      <div className="mt-2 font-bold text-[#14283A]">{tt}</div>
                      <div className="text-sm text-[#14283A]/60">{d}</div>
                    </div>
                  );
                })}
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <figure className="relative mx-auto max-w-md rotate-[3deg] bg-white p-3 pb-9 shadow-2xl">
              <span className="tape -top-3 right-10 -rotate-6" aria-hidden="true" />
              <img src={IMG.location} alt="" className="aspect-[4/5] w-full object-cover" />
              <figcaption className="font-marker absolute bottom-2 left-0 right-0 text-center text-[#14283A]">
                {ho.caption}
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* 7. Excursions */}
      <section className="bg-[#14283A] py-24 text-[#F6F1E8] sm:py-32" data-testid="section-excursions">
        <div className="mx-auto max-w-7xl px-5">
          <div className="mb-14 text-center">
            <span className="font-marker text-lg text-[#EA7A4F]">{ex.kicker}</span>
            <Reveal>
              <h2 className="mt-2 font-display text-4xl sm:text-6xl">{ex.title}</h2>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2">
            {ex.items.map(([tt, d, icon], i) => {
              const Icon = ICONS[icon] || Sun;
              return (
                <Reveal key={tt} delay={i * 0.06}>
                  <Billet notchColor="#14283A" stamp={i === 0}>
                    <div className="p-6">
                      <div className="flex items-center gap-4">
                        <span className="grad-bar flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white">
                          <Icon className="h-6 w-6" />
                        </span>
                        <div>
                          <div className="text-xs font-bold uppercase tracking-widest text-[#3F9BA6]">
                            {ex.label} 0{i + 1}
                          </div>
                          <h3 className="font-display text-xl text-[#14283A]">{tt}</h3>
                        </div>
                      </div>
                      <div className="my-4 border-t-2 border-dashed border-[#14283A]/20" />
                      <p className="text-[#14283A]/75">{d}</p>
                    </div>
                  </Billet>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
